import { ArrowDown } from 'lucide-react';

import { cn } from '@rocco/utils/cn';

/**
 * Load More (Figma 149:13459) — a white bar centred under a grid, the label at
 * the left of it and the arrow at the right, lifted on a wide soft shadow.
 *
 * Replaces the dark `filled-light` pill every paginated block on this page
 * used to carry: the same control in the same place, said quietly, because
 * "there is more below" is not the page's primary action.
 */
const CLASS = cn(
    'group flex cursor-pointer items-center justify-between rounded-[1.375rem] bg-white',
    'font-sans font-medium text-black transition-colors hover:bg-white/70',
    '[box-shadow:0px_4px_14px_10px_rgba(0,0,0,0.05)]',
    'disabled:cursor-default disabled:opacity-50',
    'max-md:h-[3.25rem] max-md:w-full max-md:pr-5 max-md:pl-4 max-md:text-base',
    // The L step (52px), where the other reveal-more controls sit — the
    // downloads' Show all and the profile's View all Projects.
    'md:h-[3.25rem] md:w-[14.375rem] md:pr-[1.625rem] md:pl-[1rem] md:text-[1rem]',
);

interface LoadMoreButtonProps {
    onClick: () => void;
    isLoading?: boolean;
    /** Spacing above the button, which differs per block. */
    className?: string;
    /** The ground it sits on. The project detail page is dark. */
    tone?: 'light' | 'dark';
}

export const LoadMoreButton = ({
    onClick,
    isLoading = false,
    className,
    tone = 'light',
}: LoadMoreButtonProps) => (
    <div className={cn('flex justify-center', className)}>
        <button
            type="button"
            onClick={onClick}
            disabled={isLoading}
            className={cn(
                CLASS,
                // The platform's one dark ink, as the button system fills it.
                tone === 'dark' &&
                    'bg-[#323232] text-white shadow-none hover:bg-[#4a4a4a]',
            )}
        >
            {isLoading ? 'Loading…' : 'Load More'}
            <ArrowDown
                aria-hidden="true"
                className={cn(
                    'shrink-0 max-md:size-5 md:size-[1.28125rem]',
                    // Plays once on hover, pointing at what the press does.
                    'group-hover:[animation:load-more-nudge_0.45s_ease-out]',
                    'motion-reduce:group-hover:[animation:none]',
                )}
            />
        </button>
    </div>
);
